import { createElement as _createElement } from "@emotion/react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { FloatingFocusManager, FloatingPortal, autoUpdate, flip, offset, size, useDismiss, useFloating, useInteractions, useListNavigation, useRole, useTypeahead, } from "@floating-ui/react";
import clsx from "clsx";
import xor from "lodash/xor";
import AutoSizer from "react-virtualized-auto-sizer";
import { Chip } from "./Chip";
import { Grid } from "./Grid";
import { MenuItem } from "./MenuItem";
import { MenuPopper } from "./MenuPopper";
import { TextInputBase, textInputBaseClasses } from "./TextInputBase";
import { CloseOutline, ExpandMoreOutline } from "../icons/icons";
import { SlotsProvider } from "../providers/SlotsProvider/SlotsProvider";
import { alpha, mergeSx } from "../system";
import { useEffect, useRef, useState, } from "react";
import { VariableSizeList } from "react-window";
import { mergeSlotProps } from "../providers/utils/slots";
import { Box } from "./Box";
export const selectClasses = {
    root: "MinervaSelect-root",
    input: "MinervaSelect-input",
    "expand-icon": "MinervaSelect-expandIcon",
    "multiselect-chip": "MinervaSelect-multiselectChip",
    "chips-container": "MinervaSelect-chipsContainer",
    dropdown: "MinervaSelect-dropdown",
    option: "MinervaSelect-option",
    "virtual-list-wrapper": "MinervaSelect-virtualListWrapper",
};
export const selectSlots = {
    option: MenuItem,
    expandIcon: ExpandMoreOutline,
    multiselectChip: Chip,
    dropdown: MenuPopper,
};
export const Select = ({ open: _open = false, value: _value, options = [], onChange, onDeselect, placeholder, multiple, optionHeight = 50, disableVirtualization, dropdownStartNode, dropdownEndNode, endNode, sx, slots, slotProps, className, onKeyDown, onClick, useValueAsKey = false, ...rest }) => {
    const [open, setOpen] = useState(_open);
    const [value, setValue] = useState(_value ?? "");
    const [activeIndex, setActiveIndex] = useState(null);
    const singleSelectOnChange = onChange;
    const multiselectOnChange = onChange;
    const navigationRef = useRef([]);
    const typeaheadRef = useRef([]);
    const { refs, floatingStyles, context } = useFloating({
        whileElementsMounted: autoUpdate,
        open,
        onOpenChange: setOpen,
        middleware: [
            offset(4),
            flip({ padding: 4 }),
            size({
                apply({ rects, availableHeight, elements }) {
                    Object.assign(elements.floating.style, {
                        maxHeight: `${availableHeight * 0.85}px`,
                        width: `${rects.reference.width}px`,
                        overflow: disableVirtualization ? "auto" : "visible",
                    });
                },
            }),
        ],
    });
    const role = useRole(context, { role: "listbox" });
    const dismiss = useDismiss(context);
    const listNav = useListNavigation(context, {
        listRef: navigationRef,
        activeIndex,
        onNavigate: setActiveIndex,
        focusItemOnHover: true,
        virtual: true,
    });
    const typeahead = useTypeahead(context, {
        listRef: typeaheadRef,
        activeIndex,
        onMatch: setActiveIndex,
        enabled: options.length <= 50,
    });
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        role,
        dismiss,
        listNav,
        typeahead,
    ]);
    useEffect(() => {
        setValue(_value ?? "");
    }, [_value]);
    useEffect(() => {
        setOpen(_open);
    }, [_open]);
    // Helper function to get value from option
    const getOptionValue = (option) => {
        if (useValueAsKey) {
            return option?.value ?? option?.label ?? "";
        }
        return option?.label ?? "";
    };
    // Helper function to find option by value
    const findOptionByValue = (searchValue) => {
        return options?.find((option) => getOptionValue(option) === searchValue);
    };
    // Helper function to get display label for a value
    const getDisplayLabel = (searchValue) => {
        const option = findOptionByValue(searchValue);
        return option?.label ?? searchValue;
    };
    return (_jsx(SlotsProvider, { baseSlots: selectSlots, slots: slots, children: ({ option: Option, expandIcon: ExpandIcon, multiselectChip: MultiselectChip, dropdown: Dropdown, ...remainingSlots }) => (_jsxs(_Fragment, { children: [_jsxs(TextInputBase, { ...getReferenceProps({
                        onClick(event) {
                            if (open) {
                                setOpen(false);
                            }
                            else {
                                setOpen(true);
                                setActiveIndex(null);
                            }
                            onClick?.(event);
                        },
                        "aria-autocomplete": "none",
                        onKeyDown(event) {
                            if (event.key === "Enter" &&
                                activeIndex != null &&
                                options[activeIndex]) {
                                if (!multiple) {
                                    setValue(options[activeIndex]?.label ?? "");
                                    setOpen(false);
                                }
                                else {
                                    const activeValue = options[activeIndex]?.label;
                                    const newValues = activeValue
                                        ? xor(value, [activeValue])
                                        : [...value];
                                    setValue("");
                                    multiselectOnChange?.(newValues);
                                    setValue(newValues);
                                    activeValue && options.length === 1 && setOpen(false);
                                }
                            }
                            onKeyDown?.(event);
                        },
                    }), rootRef: refs.setReference, as: "div", tabIndex: 0, bottomNode: typeof value !== "string" && value.length > 0 ? (_jsx(Grid, { container: true, gap: 1, className: selectClasses["chips-container"], children: value.map((selectedValue) => {
                            const selectedOption = findOptionByValue(selectedValue);
                            return (_jsx(MultiselectChip, { variant: "tonal", density: "dense", disableActiveEffect: true, disableHoverEffect: true, startNode: selectedOption?.startNode, endNode: _jsx(CloseOutline, { size: "sm" }), ...slotProps?.multiselectChip, className: clsx([
                                    selectClasses["multiselect-chip"],
                                    slotProps?.multiselectChip?.className,
                                ]), onClick: (e) => {
                                    const newValues = value.filter((v) => v !== selectedValue);
                                    multiselectOnChange?.(newValues);
                                    setValue(newValues);
                                    onDeselect?.(selectedValue);
                                    slotProps?.multiselectChip?.onClick?.(e);
                                }, children: getDisplayLabel(selectedValue) }, selectedValue));
                        }) })) : undefined, endNode: endNode ? (endNode) : (_jsx(ExpandIcon, { ...slotProps?.expandIcon, sx: {
                            transform: open ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s",
                            ...slotProps?.expandIcon?.sx,
                        }, className: clsx([
                            selectClasses["expand-icon"],
                            slotProps?.expandIcon?.className,
                        ]) })), ...rest, slots: remainingSlots, slotProps: mergeSlotProps({
                        inputRoot: {
                            onClick(e) {
                                if (open) {
                                    setOpen(false);
                                }
                                else {
                                    setOpen(true);
                                    setActiveIndex(null);
                                }
                                slotProps?.inputRoot?.onClick?.(e);
                            },
                        },
                    }, slotProps), className: clsx([selectClasses.input, className]), sx: mergeSx({
                        [`& .${textInputBaseClasses.input}`]: {
                            display: "flex",
                            alignItems: "center",
                            color: (theme) => value.length === 0 || multiple
                                ? alpha(theme.palette.background.onMain, 0.2)
                                : theme.palette.background.onMain,
                        },
                    }, sx), children: [(value.length === 0 || multiple) && placeholder, typeof value === "string" && getDisplayLabel(value)] }), open && (_jsx(FloatingPortal, { children: _jsx(FloatingFocusManager, { context: context, initialFocus: -1, visuallyHiddenDismiss: true, returnFocus: !!multiple, children: _jsxs(Dropdown, { ...getFloatingProps({
                                ref: refs.setFloating,
                                style: floatingStyles,
                            }), ...slotProps?.dropdown, className: clsx([
                                selectClasses.dropdown,
                                slotProps?.dropdown?.className,
                            ]), sx: mergeSx({
                                overflow: "hidden",
                            }, slotProps?.dropdown?.sx), children: [dropdownStartNode, !disableVirtualization ? (_jsx(Box
                                // LIMITATION: The height of the menu items is assumed to be 40px. Doing this prevents the need to measure each item's height in a pre-render pass.
                                , { 
                                    // LIMITATION: The height of the menu items is assumed to be 40px. Doing this prevents the need to measure each item's height in a pre-render pass.
                                    height: `${options.length * optionHeight}px`, width: "100%", className: selectClasses["virtual-list-wrapper"], children: _jsx(AutoSizer, { disableWidth: true, children: ({ height }) => (_jsx(VariableSizeList, { width: "100%", height: height, itemCount: options.length, itemSize: () => optionHeight, children: ({ index, style }) => {
                                                const item = options[index];
                                                const itemValue = getOptionValue(item);
                                                return item ? (_createElement(Option, { ...getItemProps({
                                                        ref: (node) => {
                                                            if (node) {
                                                                navigationRef.current[index] = node;
                                                                typeaheadRef.current[index] =
                                                                    node?.textContent ??
                                                                        null;
                                                            }
                                                        },
                                                    }), key: item?.label, role: "option", selected: (!!multiple &&
                                                        itemValue &&
                                                        value.includes(String(itemValue))) ||
                                                        (!multiple &&
                                                            value ===
                                                                String(itemValue)), focused: activeIndex === index, label: item?.label, ...item, ...slotProps?.option, style: {
                                                        ...item?.style,
                                                        ...style,
                                                    }, className: clsx([
                                                        selectClasses.option,
                                                        slotProps?.option
                                                            ?.className,
                                                    ]), onClick: (e) => {
                                                        e.stopPropagation();
                                                        if (itemValue) {
                                                            if (!multiple) {
                                                                setValue(String(itemValue));
                                                                singleSelectOnChange?.(String(itemValue));
                                                                setOpen(false);
                                                            }
                                                            else {
                                                                const newValues = xor(value, [
                                                                    itemValue,
                                                                ]);
                                                                multiselectOnChange?.(newValues.map(String));
                                                                setValue(newValues.map(String));
                                                            }
                                                        }
                                                        item?.onClick?.(e);
                                                        slotProps?.option?.onClick?.(e);
                                                    } })) : null;
                                            } })) }) })) : (options.map((item, index) => {
                                    const itemValue = getOptionValue(item);
                                    return (_createElement(Option, { ...getItemProps({
                                            ref: (node) => {
                                                if (node) {
                                                    navigationRef.current[index] = node;
                                                    typeaheadRef.current[index] =
                                                        node?.textContent ?? null;
                                                }
                                            },
                                        }), key: itemValue, role: "option", selected: (!!multiple &&
                                            itemValue &&
                                            value.includes(String(itemValue))) ||
                                            (!multiple && value === String(itemValue)), focused: activeIndex === index, label: item?.label, ...item, ...slotProps?.option, className: clsx([
                                            selectClasses.option,
                                            slotProps?.option?.className,
                                        ]), onClick: (e) => {
                                            e.stopPropagation();
                                            if (itemValue) {
                                                if (!multiple) {
                                                    setValue(String(itemValue));
                                                    singleSelectOnChange?.(String(itemValue));
                                                    setOpen(false);
                                                }
                                                else {
                                                    const newValues = xor(value, [
                                                        itemValue,
                                                    ]);
                                                    setValue("");
                                                    multiselectOnChange?.(newValues.map(String));
                                                    setValue(newValues.map(String));
                                                }
                                            }
                                            item?.onClick?.(e);
                                            slotProps?.option?.onClick?.(e);
                                        } }));
                                })), dropdownEndNode] }) }) }))] })) }));
};
//# sourceMappingURL=Select.js.map